import React from "react";
import { motion } from "framer-motion";
import {
  SearchCode,
  Brain,
  FlaskConical,
  Atom,
  DatabaseZap,
} from "lucide-react";

const UnlockingIntelligence = () => {
  return (
    <section className="mb-20 md:mb-28">
      <div className="lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center">
        <div className="mb-10 lg:mb-0">
          <h2 className="text-3xl font-bold tracking-tight text-sky-400 sm:text-4xl mb-4">
            Unlock the Hidden Intelligence in Your Chemical Data
          </h2>
          <p className="text-gray-400 text-lg mb-4">
            Vast volumes of valuable scientific data often lie dormant, locked
            away in legacy systems, spreadsheets, and lab notebooks. We've built
            a breakthrough suite of tools to revive and unify that data.
          </p>
          <p className="text-gray-400 text-lg">
            From physical archives to digital repositories, from numbers to
            molecules, our platform for chemical informatics creates smart,
            relational links between your datasets. It empowers researchers to
            run deep semantic searches, even across millions of records,
            uncovering insights that once seemed impossible.
          </p>
        </div>

        <motion.div
          className="relative w-full max-w-md mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 space-y-6 relative overflow-hidden">
            <motion.div
              whileHover={{ scale: 1.1 }}
              className="flex flex-col items-center text-center space-y-4"
            >
              <SearchCode
                className="w-20 h-20 text-sky-500 animate-pulse"
                strokeWidth={1.5}
              />
              <p className="text-gray-400 text-base">
                Turning fragmented information into a living, searchable
                knowledge base.
              </p>
              <Brain className="w-16 h-16 text-sky-500" strokeWidth={1.5} />
            </motion.div>

            <div className="flex justify-between pt-6 border-t border-gray-700">
              <motion.div whileHover={{ rotate: 10 }} className="text-sky-400">
                <FlaskConical className="w-10 h-10" strokeWidth={1.2} />
                <p className="text-xs text-center mt-1 text-gray-500">
                  Lab Data
                </p>
              </motion.div>
              <motion.div whileHover={{ rotate: -10 }} className="text-sky-400">
                <Atom className="w-10 h-10" strokeWidth={1.2} />
                <p className="text-xs text-center mt-1 text-gray-500">
                  Molecules
                </p>
              </motion.div>
              <motion.div whileHover={{ rotate: 10 }} className="text-sky-400">
                <DatabaseZap className="w-10 h-10" strokeWidth={1.2} />
                <p className="text-xs text-center mt-1 text-gray-500">
                  Data Links
                </p>
              </motion.div>
            </div>
            <motion.div
              className="absolute top-0 left-0 w-full h-full pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.05 }}
              style={{
                backgroundImage:
                  "radial-gradient(circle at center, #38bdf8 0%, transparent 70%)",
                backgroundRepeat: "no-repeat",
                backgroundSize: "400% 400%",
                animation: "pulseBG 10s ease-in-out infinite",
              }}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default UnlockingIntelligence;
